import {
  Injectable,
  computed,
  inject,
  signal
} from '@angular/core';
import { Observable, tap } from 'rxjs';

import { syncWithLocalStorage } from '@core/helpers';

import { Promocode } from '../promocodes';

import {
  ApiResponse,
  Campaign,
  ChartData,
  ChartPeriod,
  Level,
  Referrer,
  Transaction
} from './referrals.entity';
import { ReferralsService } from './referrals.service';

@Injectable({
  providedIn: 'root'
})
export class ReferralsState {
  private readonly referralsService = inject(ReferralsService);

  public readonly referrer = signal<Referrer|null>(null);
  public readonly levels = signal<Level[]>([]);
  public readonly campaigns = signal<Campaign[]>([]);
  public readonly transactions = signal<Transaction[]>([]);
  public readonly totalTransactions = signal<number>(0);
  public readonly currentCampaign = signal<Campaign|null>(null);
  public readonly minWithdrawal = signal<number|null>(null);
  public readonly depositsChart = signal<ChartData>({
    labels: [],
    series: []
  });

  public readonly registrationsChart = signal<ChartData>({
    labels: [],
    series: []
  });

  public readonly tetheredTo = signal<Referrer|null>(null);
  public readonly createdPromocodes = signal<Promocode[]>([]);
  public readonly refCode = signal<string|null>(null);

  public readonly nextLevel = computed(() => {
    const referrer = this.referrer();
    const levels = this.levels();

    if(!referrer) {
      return undefined;
    }

    return [...levels]
      .sort((a: Level, b: Level) => a.level - b.level)
      .find((level: Level) => referrer.level.level < level.level);
  });

  public readonly progress = computed(() => {
    const referrer = this.referrer();
    const nextLevel = this.nextLevel();

    if(!referrer) {
      return 0;
    }

    if(!nextLevel) {
      return 100;
    }
    
    return Math.min(referrer.referralDeposits! / nextLevel.tresholdAmount * 100, 100);
  });

  private readonly localStorageEffect = syncWithLocalStorage('referrals', {
    refCode: this.refCode
  });


  setRefCode(refCode: string|null): void {
    this.refCode.set(refCode);
  }

  loadUserStatus(): Observable<ApiResponse.UserStatus> {
    return this.referralsService.getStatus().pipe(
      tap((response) => {
        this.referrer.set(response.referrer);
        this.levels.set(response.levels);
        this.minWithdrawal.set(response.minWithdrawal);
      })
    );
  }

  loadUserCampaigns(): Observable<ApiResponse.UserCampaigns> {
    return this.referralsService.getUserCampaigns().pipe(
      tap((response) => {
        this.referrer.set(response.referrer);
        this.campaigns.set(response.campaigns);
      })
    );
  }

  loadCampaign(id: number): Observable<ApiResponse.UserCampaign> {
    return this.referralsService.getCampaign(id).pipe(
      tap((response) => {
        this.currentCampaign.set(response.campaign);
      })
    );
  }

  updateCurrentCampaignName(name: string): Observable<void> {
    const campaign = this.currentCampaign()!;

    return this.referralsService.updateCampaign(campaign.id, name).pipe(
      tap(() => {
        this.currentCampaign.set({
          ...campaign,
          name
        });
      })
    );
  }

  loadDepositsChart(interval: ChartPeriod, campaignId?: number): Observable<ChartData> {
    return this.referralsService.getFinanceChart(interval, campaignId).pipe(
      tap((response) => {
        this.depositsChart.set(response);
      })
    );
  }

  loadRegistrationsChart(interval: ChartPeriod, campaignId?: number): Observable<ChartData> {
    return this.referralsService.getRegistrationsChart(interval, campaignId).pipe(
      tap((response) => {
        this.registrationsChart.set(response);
      })
    );
  }

  loadTransactions(page: number, campaignId?: number): Observable<ApiResponse.TransactionList> {
    this.transactions.set([]);

    return this.referralsService.getTransactions(page, campaignId).pipe(
      tap((response) => {
        this.transactions.set(response.transactions);
        this.totalTransactions.set(response.total);
      })
    );
  }

  loadCreatedPromocodes(): Observable<ApiResponse.CreatedPromocodes> {
    return this.referralsService.getCreatedPromocodes().pipe(
      tap((response) => {
        this.createdPromocodes.set(response.promocodes);
      })
    );
  }

  createPromocode(
    name: string,
    uses: number,
    amountUsd: number
  ): Observable<void> {
    return this.referralsService.createPromocode(name, uses).pipe(
      tap(() => {
        const referrer = this.referrer();

        if(!referrer) {
          return;
        }
        
        this.referrer.set({
          ...referrer,
          balanceUsd: referrer.balanceUsd! - amountUsd
        });
      })
    );
  }

  applyReferralCode(referralCode: string, throwError = true): Observable<ApiResponse.TetherToCode> {
    return this.referralsService.tetherToCode(referralCode, throwError).pipe(
      tap((response) => {
        this.tetheredTo.set(response.referrer);
      })
    );
  }

  subFromReferrerBalance(amount: number): void {
    const referrer = this.referrer()!;
    
    this.referrer.set({
      ...referrer,
      balanceUsd: Number(referrer.balanceUsd) - amount
    });
  }

  reset(): void {
    this.referrer.set(null);
    this.tetheredTo.set(null);
    this.levels.set([]);
    this.createdPromocodes.set([]);
    
    this.depositsChart.set({
      labels: [],
      series: []
    });
  
    this.registrationsChart.set({
      labels: [],
      series: []
    });
  }
}